import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { useConfigActions, useConfigContext } from "../../contexts/ConfigContext/ConfigContext";
import { PATH } from "../../router/path";
import { addDatadogAction, buildLocationContext } from "../../services/datadog";
import "./AdminPage.scss";

const AdminPage: React.FC = () => {
	const navigate = useNavigate();
	const { TOGGLE_DEV_MODE, REVOKE_ADMIN_ACCESS } = useConfigActions();
	const { state: devMode } = useConfigContext((state) => state.devMode);
	const { state: adminAccessGranted } = useConfigContext((state) => state.adminAccessGranted);
	const { state: enableDevTools } = useConfigContext((state) => state.config.enableDevTools);
	const { state: location } = useConfigContext((state) => state.location);
	const { state: stall } = useConfigContext((state) => state.config.stall);

	const mountFiredRef = useRef(false);

	useEffect(() => {
		if (mountFiredRef.current) return;
		const hasAccess = !!(enableDevTools && adminAccessGranted);

		addDatadogAction("admin_page_mount", {
			...buildLocationContext(location, stall),
			hasAccess,
		});
		mountFiredRef.current = true;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Check if user has permission to access this page
	useEffect(() => {
		if (!enableDevTools || !adminAccessGranted) {
			addDatadogAction("admin_no_access_redirect", {
				...buildLocationContext(location, stall),
			});
			// Redirect to landing page if no permission or feature is disabled
			navigate(PATH.LANDING, { replace: true });
		}
	}, [adminAccessGranted, enableDevTools, navigate, location, stall]);

	const handleToggleDevMode = () => {
		const previousState = devMode;
		const newState = !devMode;

		addDatadogAction("admin_dev_mode_toggle", {
			...buildLocationContext(location, stall),
			previousState,
			newState,
		});
		TOGGLE_DEV_MODE();
	};

	const handleGoBack = () => {
		addDatadogAction("admin_back_navigation", {
			...buildLocationContext(location, stall),
		});
		REVOKE_ADMIN_ACCESS();
		navigate(-1); // Go back to the previous page
	};

	// Don't render if no permission or feature is disabled
	if (!enableDevTools || !adminAccessGranted) {
		return null;
	}

	return (
		<div className="AdminPage">
			<div className="AdminPage__container">
				<div className="AdminPage__header">
					<h1 className="AdminPage__title">Admin Panel</h1>
				</div>

				<div className="AdminPage__content">
					<div className="AdminPage__section">
						<h2 className="AdminPage__section-title">Development Mode</h2>
						<div className="AdminPage__dev-mode">
							<div className="AdminPage__dev-mode-info">
								<span className="AdminPage__dev-mode-label">Dev Mode:</span>
								<span
									className={`AdminPage__dev-mode-status ${devMode ? "AdminPage__dev-mode-status--on" : "AdminPage__dev-mode-status--off"}`}
								>
									{devMode ? "ON" : "OFF"}
								</span>
							</div>
							<button
								className={`AdminPage__toggle-btn ${devMode ? "AdminPage__toggle-btn--on" : "AdminPage__toggle-btn--off"}`}
								onClick={handleToggleDevMode}
							>
								{devMode ? "Turn OFF" : "Turn ON"}
							</button>
						</div>
					</div>

					<div className="AdminPage__section">
						<h2 className="AdminPage__section-title">Information</h2>
						<div className="AdminPage__info">
							<p className="AdminPage__info-text">
								Development mode enables additional debugging features and development tools.
							</p>
							<p className="AdminPage__info-text">
								To access this panel again, quickly tap the version number 10 times within 10
								seconds.
							</p>
						</div>
					</div>
				</div>

				<div className="AdminPage__footer">
					<button className="AdminPage__back-btn" onClick={handleGoBack}>
						Back to App
					</button>
				</div>
			</div>
		</div>
	);
};

export default AdminPage;
